import { guid } from "../../../core/helpers/string-util.js";
import Model from "../../Model.js";

/**
 * Common abstract model for all rows/columns.
 *
 * @abstract
 * @param {string} [spec.id] - Id of the item
 * @param {reportTemplate} spec.reportTemplate - Report to which this item template belongs
 * @param {boolean} [spec.hidden] - True if the row should not be visible in
 *   the report viewer or exporters.
 * @param {boolean} [spec.optional] - Optional items can be shown/hidden from the report viewer.
 * @param {boolean} [spec.visibleWhenNoData] - Set this to true when
 *   the column should be visible even if it has no data. Defaults to
 *   only showing the column if there is data to be shown.
 * @param {string} [spec.masterVisibilityTemplateId] - Id of the item whose visibility is the
 *   same as the receiver.
 */
export default class AbstractItemTemplate extends Model {
	constructor({
		id = guid(),
		reportTemplate,
		hidden,
		optional,
		visibleWhenNoData,
		masterVisibilityTemplateId,
	} = {}) {
		super(...arguments);
		this._id = id;
		this._hidden = hidden;
		this._optional = optional;
		this._visibleWhenNoData = visibleWhenNoData;
		this._masterVisibilityTemplateId = masterVisibilityTemplateId;

		// This avoids circular references when serializing this as JSON
		// as it is done in Storybook
		Object.defineProperty(this, "_reportTemplate", {
			enumerable: false,
			value: reportTemplate,
			writable: true,
		});
	}

	getLabel() {
		return this.constructor.getLabel();
	}

	getTitle() {
		return this.getLabel();
	}

	getId() {
		return this._id;
	}

	getReportTemplate() {
		return this._reportTemplate;
	}

	setReportTemplate(reportTemplate) {
		this._reportTemplate = reportTemplate;
	}

	getParent() {
		return this._reportTemplate;
	}

	getType() {
		return this.constructor.getType();
	}

	getHidden() {
		return this._hidden;
	}

	setHidden(hidden) {
		this._hidden = hidden;
	}

	setVisible(state) {
		this._hidden = !state;
	}

	/**
	 * Should return all the items of the same class from the report
	 * passed as argument.
	 */
	getAllSimilarItems({ report: _report }) {
		return this._subclassResponsibility();
	}

	instantiate(interpreter) {
		const constructor = this._instanceClass();

		return new constructor(this._interpretationSpec(interpreter));
	}

	getMasterVisibilityTemplateId() {
		return this._masterVisibilityTemplateId;
	}

	setMasterVisibilityTemplateId(id) {
		this._masterVisibilityTemplateId = id;
	}

	setOptional(value) {
		this._optional = value;
	}

	isColumn() {
		return false;
	}

	isRow() {
		return false;
	}

	/**
	 * Return true if the item is a valid formula reference.
	 *
	 * The default implementation is to check if the report template contains the
	 * receiver.
	 */
	isValidFormulaReference() {
		return this.getReportTemplate().contains(this);
	}

	isVisible() {
		return !this._hidden;
	}

	isVisibleWhenNoData() {
		return this._visibleWhenNoData;
	}

	setVisibleWhenNoData(visibleWhenNoData) {
		this._visibleWhenNoData = visibleWhenNoData;
	}

	// Get the selected visibility setting of this item for VisibilitySettingsWidget
	getVisibilitySettingId() {
		if (this.isOptional()) {
			return `optional-${this._hidden ? "hidden" : "visible"}`;
		}

		if (this.getMasterVisibilityTemplateId()) {
			return "master-visibility";
		}

		if (this._hidden) {
			return "always-hidden";
		}

		return this.isVisibleWhenNoData()
			? "always-visible"
			: "visible-if-data";
	}

	isOptional() {
		return this._optional;
	}

	canAdd(_item) {
		return this.canHaveChildren();
	}

	canHaveChildren() {
		return false;
	}

	canBeAddedToGroupRow(_group) {
		return false;
	}

	resolveReferences() {}

	/**
	 * Add `item` as a child of the receiver.
	 *
	 * When `sibling` is provided, insert `item` right after `sibling` if
	 * `sibling` is a direct child of the receiver.
	 */
	add(item, sibling) {
		return item.addTo(this, sibling);
	}

	remove() {
		return this.removeFrom(this.getParent());
	}

	addTo(_parent, _sibling) {
		return this._subclassResponsibility();
	}

	removeFrom(_parent) {
		return this._subclassResponsibility();
	}

	/**
	 * Return whether the receiver holds a reference to `item`.
	 */
	hasDependencyTo(_item) {
		return false;
	}

	accept(visitor) {
		return visitor.visitAbstractItemTemplate(this);
	}

	_instanceClass() {
		return this._subclassResponsibility();
	}

	_interpretationSpec() {
		return {
			template: this,
			hidden: this._hidden,
			visibleWhenNoData: this._visibleWhenNoData,
			masterVisibilityRowTemplateId: this._masterVisibilityTemplateId,
		};
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;
		properties.hidden = this._hidden;
		properties.optional = this._optional;
		properties.visibleWhenNoData = this._visibleWhenNoData;

		properties.masterVisibilityTemplateId =
			this._masterVisibilityTemplateId;

		return properties;
	}

	static getLabel() {
		return "";
	}

	static forType(type) {
		return this._types[type];
	}

	static isVisible() {
		return true;
	}

	static getType() {
		return "";
	}

	static registerType(type, klass) {
		this._types ||= {};

		this._types[type] = klass;
	}
}
